"use client"
import React, { ElementRef, forwardRef, cloneElement, JSX } from "react"
import { Error } from "../../icons/material-symbols/outlined/Error"
import { classNames } from "../../utils"
import { CenterAligned } from "../CenterAligned"
import { Flex } from "../Flex"
import { FlexColumn, FlexColumnProps } from "../FlexColumn"
import { Label, LabelProps } from "../Label"
import { SpaceBetween } from "../SpaceBetween"
import { Text, TextBodyProps } from "../Text"

export type FormControlProps = FlexColumnProps &
  Pick<JSX.IntrinsicElements["label"], "htmlFor"> & {
    /**
     Input for this control.
    */
    children: JSX.Element

    /**
     * Labels should always be specified for inputs for a11y - they are read by
     * screen readers when navigating to and focusing the input.
     * If the label should be visually hidden, set `hideLabel` to true.
     * - When set to `string` will be wrapped with `<Label />`
     * - When set to `function` will provide `htmlFor, required, visuallyHidden` for creating a custom `<Label />`
     */
    label:
      | React.ReactNode
      | (({ htmlFor, required, visuallyHidden }: LabelProps) => React.ReactNode)

    /** Visually hide `label` keeping it available to a11y. */
    hideLabel?: boolean

    /**
     * Caption is a single block under the input giving additional information. It shares
     * space with error/warning.
     * - When set to `string` will auto-hide if `error/warning` is set.
     * - Otherwise render as is or to right of `error/warning` if set.
     */
    caption?: React.ReactNode

    /**
     * Tip is additional information displayed under the label.
     */
    tip?: React.ReactNode

    /**
     * Displayed in red under the input to help the user validate their input.
     */
    error?: React.ReactNode

    /**
     * Displayed under the input to help inform the user of potential issues with their input.
     */
    warning?: React.ReactNode

    /**
     * Disable the input.
     */
    disabled?: boolean

    /**
     * Indicate that this field is required for validating the form.
     */
    required?: boolean

    /**
     * Display the label and input left to right, keeping `caption, error, warning` underneath.
     */
    inline?: boolean

    /**
     * Overrides applied to sub-components.
     */
    overrides?: {
      Caption?: CaptionProps
    }
  }

/**
 * Provides context such as error/required/caption/tips for form inputs.
 **/
export const FormControl = forwardRef<
  ElementRef<typeof FlexColumn>,
  FormControlProps
>(function FormControl(
  {
    children,
    label,
    htmlFor,
    caption,
    error,
    warning,
    disabled = false,
    required,
    hideLabel = false,
    tip,
    inline = false,
    overrides = {},
    ...rest
  },
  ref,
) {
  const onlyChildrenProps = children.props
  const isCaptionString = typeof caption === "string"
  const isLabelFunction = typeof label === "function"
  const errorElement = error || warning
  const htmlForWithFallback = htmlFor || onlyChildrenProps.id

  const childrenProps: { disabled?: boolean; error?: boolean } = {}
  if (onlyChildrenProps.disabled || !!disabled) {
    childrenProps["disabled"] = true
  }
  if (onlyChildrenProps.error || !!error) {
    childrenProps["error"] = true
  }
  const content = cloneElement(children, childrenProps)

  return (
    <FlexColumn {...rest} ref={ref}>
      <Flex
        className={
          inline ? "flex-row items-center justify-between" : "flex-col"
        }
      >
        <FlexColumn className={hideLabel || inline ? undefined : "mb-3"}>
          {isLabelFunction ? (
            label({
              htmlFor: htmlForWithFallback,
              required,
              visuallyHidden: hideLabel,
            })
          ) : (
            <Label
              className="w-full"
              htmlFor={htmlForWithFallback}
              required={required}
              visuallyHidden={hideLabel}
            >
              {label}
            </Label>
          )}
          {tip && (
            <Text.Body className="mt-1 text-text-secondary" size="xs">
              {tip}
            </Text.Body>
          )}
        </FlexColumn>
        {inline ? <CenterAligned>{content}</CenterAligned> : content}
      </Flex>

      <SpaceBetween className="mt-1">
        {errorElement && (
          <Caption
            {...overrides.Caption}
            aria-describedby={htmlForWithFallback}
            error={!!error}
            role="status"
            warning={!!warning}
          >
            <Error
              className={classNames(
                "mr-1 !leading-[inherit]",
                warning ? "text-warning" : undefined,
              )}
              size={14}
              title={error ? "Error" : "Warning"}
            />
            <span>{errorElement}</span>
          </Caption>
        )}
        {isCaptionString
          ? !errorElement && (
              <Caption
                {...overrides.Caption}
                aria-describedby={htmlForWithFallback}
              >
                {caption}
              </Caption>
            )
          : caption}
      </SpaceBetween>
    </FlexColumn>
  )
})

type CaptionProps = TextBodyProps & {
  /** Indicate the importance of this caption in red.  */
  error?: boolean

  /** Warnings increase visibility of text, error formatting will take presence over warnings. */
  warning?: boolean
}

const Caption = ({ className, error, warning, ...rest }: CaptionProps) => (
  <Text.Body
    className={classNames(
      "flex w-full items-center",
      error && "text-error",
      warning && !error && "text-text-primary",
      className,
    )}
    color="text-secondary"
    size="sm"
    {...rest}
  />
)
